import { AnyObject, FORM_ERROR } from 'final-form';
import { isEqual } from 'lodash';
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { OrganizationAddressRequest, Physician, useCreateAddress, useCreatePhysician, useEnrollment } from '../api';
import { PrivateLayout, SelectedServiceRequestContent } from '../components';
import { FeatureGuard } from '../components/multi-tenant/FeatureGuard';
import { useDrugFeatureKeyPrefix } from '../hooks/use-drug-feature-key-prefix';
import { AnyType, AutomatedServiceOptionType, ServiceOptionType } from '../interfaces';
import {
  AppRootState,
  navigateToRequestsSubmitted,
  navigateToRxModule,
  navigateToServiceRequest,
  tenancyFeatureSelector
} from '../redux';
import { useGeneratePDF } from '../api';
import { usePatientInfoQuery } from '../graphql';
import { FIELD_NAMES, getEnrollmentPayload, RX_MODULES } from '../utils';
import { getInitialFormValues } from '../utils/dynamic-forms/service-request-fields/original-values';
import { COPAY_CLAIM_DEFAULT_FILE_NAME } from '../../src/utils/constants';

interface SelectedServiceRequestViewParams {
  patientId: string;
  service: ServiceOptionType;
}

export const SelectedServiceRequestView = React.memo(
  () => {
    const dispatch = useDispatch();
    const { mutate: enroll } = useEnrollment({});
    const user = useSelector((state: AppRootState) => state.auth.session?.user);
    const userDetails = useSelector((state: AppRootState) => state.auth.session?.userDetails);
    const orgDetails = useSelector((state: AppRootState) => state.auth.session?.orgDetails);
    const userInfo = useSelector((state: AppRootState) => state.auth.session?.userInfo);
    const { service, patientId } = useParams<SelectedServiceRequestViewParams>();
    const patientResult = usePatientInfoQuery({
      variables: { patientId: patientId?.toString() ?? '' },
      skip: !patientId
    });
    const patientData = patientResult?.data?.result;
    const { mutate: createPhysician } = useCreatePhysician({});
    const { mutate: createLocation } = useCreateAddress({});
    const { mutate: generatePdf } = useGeneratePDF({});
    const { prescriberFields } = FIELD_NAMES;
    const featureKeyPrefix = useDrugFeatureKeyPrefix();
    const { search: queryString } = useLocation();
    const drugList = useSelector((state: AppRootState) => state.app?.entities?.drugList) ?? null;
    const queryParams = new URLSearchParams(queryString);
    const autoServiceOption: AnyType = useSelector<AnyType>(tenancyFeatureSelector('autoServiceOption'));
    if (autoServiceOption?.enabled) {
      for (const [key, value] of queryParams.entries()) {
        if (key == 'prescriberId') {
          sessionStorage.setItem('prescriberId', value);
        }
        if (key == 'locationId') {
          sessionStorage.setItem('locationId', value);
        }
      }
      sessionStorage.setItem('patientId', patientId);
    }
    const drugId = queryParams.get('drug') || drugList?.[0]?.DrugId?.toString() || '';

    const formSchema: AnyType = useSelector<AnyType>(
      tenancyFeatureSelector(`${featureKeyPrefix}form.service-request-${service}`)
    );
    const portalName: AnyType = useSelector<AnyType>((state: AppRootState) => state.app?.config?.applicationName);
    const autoMedEBV: AnyType = useSelector<AnyType>(tenancyFeatureSelector(AutomatedServiceOptionType.AUTO_MED_EBV));
    const onSubmit = useCallback(
      (values: AnyObject) => {
        const asyncSubmit = async () => {
          let routerState: AnyObject = {};
          const submitPayload = getEnrollmentPayload(
            service === 'copay-claim' ? getInitialFormValues() : values,
            patientId,
            service,
            portalName,
            user,
            formSchema?.fields,
            drugId,
            service === ServiceOptionType.CopayClaim //this will tell to merge the additional documents in the payload
          );

          if (
            service === ServiceOptionType.PharmacyElectronicPriorAuthorization ||
            service === ServiceOptionType.PharmacyElectronicBenefitVerification ||
            service === ServiceOptionType.MedicalElectronicBenefitVerification ||
            service === ServiceOptionType.MedicalElectronicPriorAuthorization
          ) {
            const isNewPrescriber = values[prescriberFields.isNewPrescriber];
            const isNewLocationAddress = values[prescriberFields.isNewLocationAddress];

            try {
              if (isNewPrescriber == true) {
                const payload: Physician = {
                  firstName: values[prescriberFields.firstName],
                  lastName: values[prescriberFields.lastName],
                  licenseState: values[prescriberFields.licensingState],
                  middleName: values[prescriberFields.middleName],
                  npi: values[prescriberFields.npi] && values[prescriberFields.npi]?.toString(),
                  stateLicenseNumber: values[prescriberFields.stateLicenseNumber],
                  taxId: values[prescriberFields.taxId],
                  ptan: values[prescriberFields.ptan],
                  dea: values[prescriberFields.dea],
                  email: values[prescriberFields.email]
                };

                const result = await createPhysician(payload);
                if (result.errors && result.errors.length > 0) {
                  const errors = result.errors?.map((e) => e.description).join(' ');
                  // Return the form error to display
                  return { [FORM_ERROR]: errors };
                }
              }
              if (isNewLocationAddress) {
                const locationPayload: OrganizationAddressRequest = {
                  addressType: 'Other',
                  address1: String(values[prescriberFields.locationAddress1]),
                  address2: values[prescriberFields.locationAddress2] as string,
                  city: String(values[prescriberFields.locationAddressCity]),
                  state: String(values[prescriberFields.locationAddressState]),
                  zip: String(values[prescriberFields.locationAddressZip]),
                  phone1: String(values[prescriberFields.locationPhone1]),
                  phone1Type: 'Work',
                  fax: String(values[prescriberFields.locationFaxNumber])
                };
                const locationResult = await createLocation(locationPayload);
                if (locationResult.errors && locationResult.errors.length > 0) {
                  const errors = locationResult.errors?.map((e) => e.description).join(' ');
                  return { [FORM_ERROR]: errors };
                }
              }
            } catch (e: any) {
              // TODO: validate the messages that _could_ show here.
              return { [FORM_ERROR]: e.message };
            }
          }
          if (RX_MODULES.includes(service)) {
            return dispatch(
              navigateToRxModule(patientId, service, {
                enrollmentPayload: submitPayload ?? {},
                isMedEbvAutomationEnabled: false
              })
            );
          }

          try {
            // Grab the payload for submission
            const payLoadForSubmit = submitPayload;
            // If the service is Copay Claim, generate the tagged PDF with attached docs having updated values
            if (service === ServiceOptionType.CopayClaim) {
              const pdfPayload = getEnrollmentPayload(
                values,
                patientId,
                service,
                portalName,
                user,
                formSchema?.fields,
                drugId,
                service === ServiceOptionType.CopayClaim
              );
              const pdfResponse = await generatePdf(pdfPayload);
              if (pdfResponse != null && payLoadForSubmit.other) {
                // Add the generated PDF to the additional documents array of payload for submission for Copay Claim submission
                payLoadForSubmit.other.additionalDocuments = [];
                payLoadForSubmit.other.additionalDocuments.push({
                  fileData: String(pdfResponse),
                  fileName: COPAY_CLAIM_DEFAULT_FILE_NAME
                });
              }
            }
            const result = await enroll(payLoadForSubmit);

            if (result.errors && result.errors.length > 0) {
              const errors = result.errors.map((e) => e.description).join(' ');

              return { [FORM_ERROR]: errors };
            }

            if (service === ServiceOptionType.PatientResources) {
              const { patientFields } = FIELD_NAMES;
              routerState = {
                patientName: `${values[patientFields.firstName]} ${values[patientFields.lastName]}`,
                email: values[patientFields.email]
              };
            } else {
              routerState = {
                patientId: patientId,
                enrollmentPayload: submitPayload ?? {},
                isMedEbvAutomationEnabled: autoMedEBV?.enabled,
                loadingService: ServiceOptionType.MedicalElectronicBenefitVerification,
                enrollmentResult: {
                  enrollmentId: result?.enrollmentId,
                  serviceRequestId: result?.serviceRequestId
                },
                allPAExecuted: false
              };
            }

            if (autoMedEBV?.enabled && routerState?.enrollmentPayload?.insurance?.hasPatientInsurance) {
              dispatch(
                navigateToRxModule(
                  routerState?.patientId,
                  ServiceOptionType.MedicalElectronicBenefitVerification,
                  routerState
                )
              );
            } else {
              if (service === ServiceOptionType.CopayClaim) {
                return;
              } else {
                dispatch(navigateToRequestsSubmitted(service, routerState));
              }
            }
          } catch (e: any) {
            // TODO: validate the messages that _could_ show here.
            return { [FORM_ERROR]: e.message };
          }
        };

        return asyncSubmit();
      },
      [patientId, service, user]
    );
    const serviceTitleMap: Record<string, string> = {
      [ServiceOptionType.PharmacyElectronicPriorAuthorization]: 'Pharmacy ePA Request',
      [ServiceOptionType.PharmacyElectronicBenefitVerification]: 'Pharmacy eBV Request',
      [ServiceOptionType.MedicalElectronicBenefitVerification]: 'Medical eBV Request',
      [ServiceOptionType.MedicalElectronicPriorAuthorization]: 'Medical ePA Request',
      [ServiceOptionType.PatientResources]: 'Patient Resources',
      [ServiceOptionType.CopayClaim]: 'Copay Claim Submission'
      // Add more as needed
    };

    const pageTitle = serviceTitleMap[service] || 'Service Request';
    const pageIcon = (
      <i
        className='fa-light fa-circle-arrow-left'
        role='button'
        tabIndex={0}
        onClick={() => dispatch(navigateToServiceRequest())}
        style={{
          cursor: 'pointer',
          fontSize: 24,
          color: '#009B92'
        }}
        aria-label='Go back'
      />
    );
    return (
      <FeatureGuard
        contentKey={`${featureKeyPrefix}service-request.${service}`}
        disabledHandler={() => dispatch(navigateToServiceRequest())}>
        <PrivateLayout
          pageTitle={pageTitle}
          pageIcon={service ? pageIcon : undefined}
          pageTitleClassName='service-page-title'
          subtoolbarChildren={
            <strong className='text-capitalize text-black' style={{ fontSize: '24px' }}>
              {patientData ? `${patientData?.lastName}, ${patientData?.firstName}` : ''}
            </strong>
          }>
          <SelectedServiceRequestContent
            onSubmit={onSubmit}
            service={service}
            userDetails={userDetails}
            orgDetails={orgDetails}
            userInfo={userInfo}
          />
        </PrivateLayout>
      </FeatureGuard>
    );
  },
  (prev, next) => isEqual(next, prev)
);

SelectedServiceRequestView.displayName = 'SelectedServiceRequestView';
